import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'
import { Loader2, Search } from 'lucide-react'

interface SearchResult {
  id: number
  name: string
  project_name: string
  org_name: string
}

interface ProjectSearchPopoverProps {
  onSelect: (name: string) => void
}

export function ProjectSearchPopover({ onSelect }: ProjectSearchPopoverProps) {
  const { t } = useTranslation('admin')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 2) {
      setResults([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await fetch(`/admin/templates/search_projects?q=${encodeURIComponent(query)}`)
        if (response.ok) {
          const data = await response.json()
          setResults(data)
        }
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, open])

  const handleSelect = (result: SearchResult) => {
    onSelect(result.name)
    setOpen(false)
    setQuery('')
    setResults([])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" className="shrink-0" aria-label="Search projects">
          <Search className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            placeholder={t('templates.form.searchProjects')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isSearching && query.length >= 2 && results.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t('templates.form.noResults')}
            </p>
          )}
          {!isSearching && query.length < 2 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t('templates.form.typeToSearch')}
            </p>
          )}
          {results.map((result) => (
            <button
              key={result.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent cursor-pointer"
              onClick={() => handleSelect(result)}
            >
              <div className="flex flex-col items-start min-w-0 flex-1">
                <span className="truncate w-full">{result.project_name}</span>
                <span className="text-xs text-muted-foreground truncate w-full">{result.org_name}</span>
              </div>
              <span className="text-xs text-muted-foreground truncate shrink-0">{result.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
